import { DatePipe, Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppSharedModule } from 'app/app-shared.module';
import { JohnDeereService, JohnDeereStatus } from 'app/core/services/john-deere.service';
import { AgresSpinnerService } from 'app/shared/components/agres-spinner/agres-spinner.service';
import { ToastrService } from 'ngx-toastr';
import swal from 'sweetalert2';

@Component({
  selector: 'app-john-deere-integration',
  standalone: true,
  imports: [AppSharedModule, DatePipe],
  templateUrl: './john-deere-integration.component.html',
  styleUrl: './john-deere-integration.component.scss',
})
export class JohnDeereIntegrationComponent implements OnInit {
  status: JohnDeereStatus = { connected: false };

  readonly OC_CONNECTIONS_URL = 'https://connections.deere.com/';

  private readonly TRUSTED_JD_AUTH_HOSTNAME = 'signin.johndeere.com';
  private readonly TOAST_POSITION = 'toast-top-full-width';

  constructor(
    private johnDeereService: JohnDeereService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private toastr: ToastrService,
    private translate: TranslateService,
    private agresSpinner: AgresSpinnerService,
  ) {}

  ngOnInit(): void {
    const connected = this.route.snapshot.queryParamMap.get('connected');
    const error = this.route.snapshot.queryParamMap.get('error');

    // Remove query params from URL without reloading
    this.router.navigate([], { replaceUrl: true, queryParams: {} });

    if (connected === 'true') {
      this.translate.get('connections.oc.connect-success').subscribe((msg: string) => {
          this.toastr.success(msg, '', { positionClass: this.TOAST_POSITION });
      });
      this.loadStatus();
    } else if (connected === 'pending') {
      // The user just came back from John Deere's organization picker in Operations
      // Center — re-check which organization (if any) they actually approved.
      this.finalizeConnection();
    } else if (error) {
      this.translate.get('connections.oc.connect-error').subscribe((msg: string) => {
          this.toastr.error(msg, '', { positionClass: this.TOAST_POSITION });
      });
      this.loadStatus();
    } else {
      this.loadStatus();
    }
  }

  goBack(): void {
    this.location.back();
  }

  disconnect(): void {
    this.translate
      .get(['connections.oc.disconnect-confirm-title', 'connections.oc.disconnect-confirm-text'])
      .subscribe((t) => {
        swal
          .fire({
            title: t['connections.oc.disconnect-confirm-title'],
            text: t['connections.oc.disconnect-confirm-text'],
            icon: 'warning',
            showCancelButton: true,
          })
          .then((result) => {
            if (!result.isConfirmed) return;
            this.johnDeereService.disconnect().subscribe({
              next: () => {
                this.status = { connected: false };
                this.toastr.success(
                  this.translate.instant('connections.oc.disconnect-success'),
                  '',
                  { positionClass: this.TOAST_POSITION },
                );
              },
              error: () => {
                this.toastr.error(
                  this.translate.instant('connections.oc.disconnect-error'),
                  '',
                  { positionClass: this.TOAST_POSITION },
                );
              },
            });
          });
      });
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (!document.hidden && (this.status.connected || this.status.pending)) {
      this.loadStatus(true);
    }
  }

  connect(): void {
    this.johnDeereService.getAuthorizationUrl().subscribe({
      next: ({ authorizationUrl }) => {
        if (this.isTrustedAuthorizationUrl(authorizationUrl)) {
          window.location.href = authorizationUrl;
        } else {
          this.toastr.error(
            this.translate.instant('connections.oc.invalid-url'),
            '',
            { positionClass: this.TOAST_POSITION },
          );
        }
      },
      error: () => {
        this.toastr.error(
          this.translate.instant('connections.oc.auth-url-error'),
          '',
          { positionClass: this.TOAST_POSITION },
        );
      },
    });
  }

  private finalizeConnection(): void {
    this.translate
      .get('connections.oc.loading')
      .subscribe((message: string) => {
        this.agresSpinner.toShow(message);
      });
    this.johnDeereService.finalizeConnection().subscribe({
      next: (result) => {
        if (result.connected) {
          this.translate
            .get('connections.oc.connect-success')
            .subscribe((msg: string) => {
              this.toastr.success(msg, '', {
                positionClass: this.TOAST_POSITION,
              });
            });
        }
        this.loadStatus(true);
        this.agresSpinner.toHide();
      },
      error: () => {
        this.agresSpinner.toHide();
        this.translate
          .get('connections.oc.connect-error')
          .subscribe((msg: string) => {
            this.toastr.error(msg, '', {
              positionClass: this.TOAST_POSITION,
            });
          });
        this.loadStatus();
      },
    });
  }

  private isTrustedAuthorizationUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return (
        parsed.protocol === 'https:' &&
        parsed.hostname === this.TRUSTED_JD_AUTH_HOSTNAME
      );
    } catch {
      return false;
    }
  }

  private loadStatus(silent = false): void {
    if (!silent) {
      this.translate.get('connections.oc.loading').subscribe((message: string) => {
        this.agresSpinner.toShow(message);
      });
    }
    this.johnDeereService.getStatus().subscribe({
      next: (s) => {
        this.status = s;
      },
      complete: () => {
        if (!silent) {
          this.agresSpinner.toHide();
        }
      },
      error: () => {
        if (!silent) {
          this.agresSpinner.toHide();
          this.toastr.error(
            this.translate.instant('connections.oc.status-error'),
            '',
            { positionClass: this.TOAST_POSITION },
          );
        }
      },
    });
  }
}
